import { BonePose, clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * Cantor de huesos (models/bonesinger.ts). Idle and walk loop: it sways and hums, the long jaw working; the walk is
 * a shuffling glide inside the robe, the spine-staff planted at every other step. Attack: it comes in cocked, staff
 * drawn back, and brings the bell end down on the foe (the blow lands at ~0.13 s); hit; stun (loop, the jaw slack);
 * die (the song breaks, the light goes out, the bones drop out of the air, the skull tips off and it all collapses
 * into the robe, the staff toppling); and its power, `raise` (Levantar): head thrown back singing, staff and claw
 * raised, the chest light flaring (`core`), the turning bones drawn up and spun fast (`orbit`).
 *
 * Conventions as in human.ts: a negative X swings an arm forward and up; +X on a trunk leans it forward; +X on the
 * jaw opens it; +X on the staff tips its head forward; the figure faces +Z and its `R` limbs are on +X. The bell hangs
 * plumb from the staff's hook and swings late (see `trail`). The root never moves (the sigil and the bones on the
 * ground hang from it): the figure sways, bobs and sinks on `body`, and `orbit` hangs from the root so it can fall flat.
 */

type B = 'root' | 'body' | 'robe' | 'chest' | 'core' | 'head' | 'jaw' | 'armL' | 'foreL' | 'staff' | 'bell' | 'armR' | 'foreR' | 'orbit';
type P = KeyPose<B>;
type Pose = (b: Bones<B>, t: number) => void;

/** A pose sheet that makes any bone it is asked for (for sampling a pose at another time). */
const scratch = (): Bones<B> => {
  const o: Record<string, BonePose> = {};
  return new Proxy(o, { get: (_, k: string) => (o[k] ??= new BonePose()) }) as Bones<B>;
};

/** A bone that trails its chain: `hang` cancels that much of the chain's tilt (1 hangs plumb), `k` swings it against the motion. */
interface Trail {
  bone: B;
  chain: readonly B[];
  lag: number;
  k: number;
  hang: number;
}

const TRAILS: readonly Trail[] = [
  { bone: 'bell', chain: ['root', 'body', 'chest', 'armL', 'foreL', 'staff'], lag: 0.08, k: 1.5, hang: 1 },
  { bone: 'robe', chain: ['root', 'body'], lag: 0.12, k: 0.5, hang: 0.4 },
];

/** Secondary motion (as in mourner.ts): each trailing bone swings against how its chain moved a beat earlier. */
const trail = (pose: Pose, dur: number, loop: boolean): Pose => (b, t) => {
  pose(b, t);
  const wrap = (x: number): number => (loop ? ((x % dur) + dur) % dur : Math.max(0, x));
  const at = (x: number): Bones<B> => {
    const s = scratch();
    pose(s, wrap(x));
    return s;
  };
  for (const f of TRAILS) {
    const s1 = at(t - f.lag), s2 = at(t - 2 * f.lag);
    const sum = (bb: Bones<B>, ax: 'rx' | 'rz'): number => f.chain.reduce((a, n) => a + bb[n][ax], 0);
    for (const ax of ['rx', 'rz'] as const) {
      const c0 = sum(b, ax), c1 = sum(s1, ax), c2 = sum(s2, ax);
      b[f.bone][ax] += -f.hang * c0 - f.k * (c0 - c1) + 0.55 * f.k * (c1 - c2);
    }
  }
};

const mclip = (name: string, dur: number, loop: boolean, pose: Pose, squash?: (t: number) => [number, number, number], label?: string): Clip<B> =>
  clip<B>(name, dur, loop, trail(pose, dur, loop), squash, label);

/** Bowed over its song, the jaw hanging a little open. */
const ST: P = { chest: [0.05, 0, 0], head: [0.06, 0, 0], jaw: [0.06, 0, 0], orbit: [0.22, 0, 0.1] };

const idle = (): Clip<B> =>
  mclip('idle', 4, true, (b, t) => {
    const p = phase(t, 4) * TAU;
    apply(b, ST);
    const sw = Math.sin(p);
    // A slow sway from side to side, as if rocking to its own dirge; the head nods the beat.
    b.body.rot(0, 0, 0.035 * sw);
    b.body.rot(0.02 * Math.sin(p * 2), 0.05 * Math.sin(p - 0.4), -0.02 * sw);
    b.chest.rot(0.03 * Math.sin(p * 2 + 0.6), 0.06 * Math.sin(p - 0.8), -0.03 * sw);
    b.head.rot(0.06 * Math.sin(p * 4 + 0.5), 0.1 * Math.sin(p - 1.2), 0.08 * Math.sin(p - 0.6));
    // The hum: the jaw works in long, uneven syllables.
    b.jaw.rot(0.1 + 0.08 * Math.sin(p * 4) + 0.05 * Math.sin(p * 6 + 1), 0, 0.03 * Math.sin(p * 2));
    b.armL.rot(0.04 * Math.sin(p + 0.5), 0, 0.03 * sw);
    b.staff.rot(0.03 * Math.sin(p - 0.3), 0, -0.04 * sw);
    // The claw beckons, the fingers kneading the air.
    b.armR.rot(-0.08 * Math.sin(p + 1.5), 0, 0.06 * Math.sin(p * 2));
    b.foreR.rot(-0.12 * Math.max(0, Math.sin(p * 2 + 1)) ** 2, 0, 0.05 * Math.sin(p * 2));
    // The light breathes with the song; the bones go round once.
    b.core.scale(1 + 0.12 * Math.sin(p * 4) + 0.06 * Math.sin(p * 6 + 1));
    b.orbit.rot(0, p, 0).pos(0, 0.03 * Math.sin(p * 2), 0);
  }, (t) => {
    const br = Math.sin(phase(t, 4) * TAU * 2);
    return [1 + 0.008 * br, 1 - 0.008 * br, 1 + 0.008 * br];
  });

/** A shuffle inside the robe: two short steps a loop, the staff planted on the first, the hem rocking. */
const walk = (dur = 1.4): Clip<B> =>
  mclip('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), c2 = Math.cos(2 * th);
    apply(b, ST);
    b.body.pos(0, 0.016 * c2 - 0.01, 0).rot(0, 0, 0.04 * sn);
    b.body.rot(0.06, 0.08 * sn, 0);
    b.chest.rot(0.08 + 0.03 * Math.cos(2 * th - 0.6), -0.07 * sn, -0.03 * sn);
    b.head.rot(-0.04 - 0.03 * Math.cos(2 * th - 1), -0.03 * sn, -0.03 * sn);
    b.jaw.rot(0.08 + 0.06 * Math.max(0, Math.sin(2 * th + 0.5)));
    // The staff: swung forward and planted (its foot strikes at th = 0), then the body shuffles up to it.
    const reach = Math.cos(th);
    b.armL.rot(-0.16 * reach - 0.05, 0, 0.02);
    b.staff.rot(0.12 * reach + 0.05 - 0.05 * Math.max(0, Math.sin(th)) ** 3, 0, 0.03 * sn);
    b.armR.rot(0.12 * Math.sin(th - 0.4), 0, 0.04);
    b.foreR.rot(-0.08 * Math.max(0, -Math.sin(th - 0.8)));
    b.core.scale(1 + 0.08 * c2);
    b.orbit.rot(0, th, 0).pos(0, 0.02 * Math.sin(th * 2), 0);
  }, (t) => {
    const th = phase(t, dur) * TAU;
    // A jolt as the staff bites the ground.
    const k = Math.max(0, Math.cos(th)) ** 8;
    return [1 + 0.012 * k, 1 - 0.015 * k, 1 + 0.012 * k];
  });

/** Cocked: the staff drawn back over the shoulder, the body twisted away. Struck: brought down on the foe. */
const COCK: P = { body: [-0.06, 0.15, 0, 0, 0.02, -0.03], chest: [-0.2, 0.3, 0], head: [-0.15, -0.2, 0], jaw: [0.3, 0, 0], armL: [-1.25, 0, -0.35], foreL: [-0.2, 0, 0], staff: [-0.75, 0, 0.1], armR: [-0.5, 0, 0.4] };
const STRIKE: P = { body: [0.15, -0.12, 0, 0, -0.03, 0.1], chest: [0.4, -0.2, 0], head: [0.25, 0.1, 0], jaw: [0.45, 0, 0], armL: [-0.95, 0, 0.1], foreL: [-0.15, 0, 0], staff: [2.0, 0, -0.05], armR: [0.25, 0, -0.1], robe: [0.05, 0, 0] };

const attack = (): Clip<B> =>
  mclip('attack', 0.8, false, (b, t) => {
    timeline(b, t, [
      [0, COCK],
      [0.13, STRIKE, inCubic],
      [0.32, { ...STRIKE, chest: [0.36, -0.18, 0], staff: [1.85, 0, -0.05] }, outBack],
      [0.8, ST, inOut],
    ], { head: 0.02, foreR: 0.03, staff: 0.01 });
    if (t > 0.13) {
      // The shock up the staff.
      const k = settle(t - 0.13, 6, 12);
      b.staff.rot(-0.08 * k);
      b.chest.rot(0.03 * k);
    }
    b.core.scale(1 + 0.4 * envelope(t, 0.05, 0.13, 0.2, 0.45));
  }, (t) => {
    const k = t > 0.13 ? Math.max(0, settle(t - 0.13, 3, 10)) : 0;
    return [1 + 0.03 * k, 1 - 0.04 * k, 1 + 0.03 * k];
  });

const hit = (): Clip<B> =>
  mclip('hit', 0.4, false, (b, t) => {
    apply(b, ST);
    const k = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.body.rot(-0.12 * k, 0.08 * k);
    b.chest.rot(-0.28 * k, 0.12 * k, 0.05 * k);
    b.head.rot(-0.35 * k, -0.18 * k);
    b.jaw.rot(0.35 * Math.abs(k));
    b.armR.rot(-0.3 * k, 0, 0.2 * k);
    b.armL.rot(0.1 * k);
    b.staff.rot(-0.15 * k);
    b.body.pos(0, 0, -0.06 * k);
    b.core.scale(1 - 0.4 * Math.abs(k));
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.05 * k, 1 - 0.06 * k, 1 + 0.05 * k];
  });

/** Dazed: the song gone out of it, the jaw slack, it reels on its staff; the bones drift and sag. */
const stun = (): Clip<B> =>
  mclip('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    apply(b, ST, 0.5);
    b.body.rot(0.04 * Math.sin(p), 0, 0.08 * Math.cos(p));
    b.chest.rot(0.32 + 0.06 * Math.sin(p * 2), 0, -0.08 * Math.cos(p));
    b.head.rot(0.3 + 0.2 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.25 * Math.sin(p + 0.8));
    b.jaw.rot(0.4 + 0.1 * Math.sin(p * 2 + 0.3));
    b.armL.rot(0.1, 0, 0.12 + 0.05 * Math.sin(p));
    b.staff.rot(0.12 * Math.sin(p + 0.5), 0, -0.15 - 0.08 * Math.cos(p));
    b.armR.rot(0.4 + 0.1 * Math.sin(p + 1.2), 0, -0.25);
    b.foreR.rot(0.3);
    b.core.scale(0.55 + 0.15 * Math.sin(p * 3));
    b.orbit.rot(0.15 * Math.sin(p), p, 0).pos(0, -0.18, 0);
  });

/**
 * The song breaks: it shrieks, head and claw flung up; the light gutters out and the bones drop from the air; then
 * it all comes apart: the skull tips forward off the neck, the ribs sink into the robe and the robe slumps to a
 * heap, the staff toppling to the ground beside it.
 */
const die = (): Clip<B> =>
  mclip('die', 1.9, false, (b, t) => {
    const heap: P = {
      robe: [0, 0, 0, 0, 0.18, 0, 0.5], body: [0.25, 0, 0.05, 0, -0.6, 0.05], chest: [0.9, 0.1, 0.15, 0, -0.06, 0, 0.88],
      head: [0.7, 0.6, 0.5], jaw: [0.9, 0, 0.2], armL: [1.0, 0, -0.9], foreL: [0.5, 0, 0], staff: [-1.1, 0, 0.2], bell: [-1.5, 0, 0],
      armR: [-0.6, 0, 0], foreR: [0.3, 0, 0], core: [0, 0, 0, 0, 0, 0, 0.02], orbit: [0, 1.2, 0, 0, -0.95, 0],
    };
    timeline(b, t, [
      [0, ST],
      [0.2, { body: [-0.12, 0, 0, 0, 0.04, 0], chest: [-0.4, 0, 0], head: [-0.7, 0, 0], jaw: [0.7, 0, 0], armL: [-0.3, 0, -0.1], staff: [0.75, 0, 0], armR: [-2.0, 0, 0.5], foreR: [-0.3, 0, 0], core: [0, 0, 0, 0, 0, 0, 1.8], orbit: [0, 0.6, 0, 0, 0.06, 0] }, outExpo],
      [0.5, { body: [-0.1, 0, 0, 0, 0.04, 0], chest: [-0.36, 0, 0], head: [-0.62, 0.1, 0], jaw: [0.75, 0, 0.1], armL: [-0.25, 0, -0.1], staff: [0.68, 0, 0], armR: [-1.85, 0, 0.5], foreR: [-0.35, 0, 0], core: [0, 0, 0, 0, 0, 0, 1.2], orbit: [0, 0.9, 0, 0, 0.04, 0] }],
      [0.85, { robe: [0, 0, 0, 0, 0.1, 0, 0.72], body: [0.1, 0, 0, 0, -0.28, 0], chest: [0.5, 0.05, 0.05, 0, -0.03, 0, 0.92], head: [0.5, 0.25, 0.1], jaw: [0.6, 0, 0.1], armL: [0.3, 0, -0.3], staff: [0.15, 0, 0.3], armR: [-0.4, 0, -0.3], core: [0, 0, 0, 0, 0, 0, 0.3], orbit: [0, 1.1, 0, 0, -0.95, 0] }, inQuad],
      [1.35, heap, inQuad],
      [1.9, { ...heap, chest: [0.95, 0.1, 0.15, 0, -0.06, 0, 0.88] }, outBack],
    ], { head: 0.08, jaw: 0.1, armR: 0.04, armL: 0.03, staff: 0.12, orbit: 0 });
    // The shriek shakes it; the bones hit the ground and bounce once.
    const tr = envelope(t, 0.18, 0.25, 0.45, 0.55) * Math.sin(t * 70);
    b.chest.rot(0, 0.03 * tr, 0.02 * tr);
    b.head.rot(0.04 * tr);
    if (t > 0.85) b.orbit.pos(0, 0.05 * Math.max(0, -settle(t - 0.85, 2.5, 7)), 0);
  }, (t) => {
    const k = t > 1.35 ? Math.max(0, settle(t - 1.35, 3, 9)) : 0;
    return [1 + 0.06 * k, 1 - 0.07 * k, 1 + 0.06 * k];
  });

/**
 * The raising: it crouches over the song, then throws its head back, the jaw wide, staff and claw thrust at the sky;
 * the chest light flares past full and the bones are drawn up and spun round it; it holds the note and sinks back.
 */
const raise = (): Clip<B> =>
  mclip('raise', 1.3, false, (b, t) => {
    const up: P = {
      body: [-0.08, 0, 0, 0, 0.05, 0], robe: [-0.03, 0, 0], chest: [-0.32, 0, 0], head: [-0.75, 0, 0], jaw: [0.75, 0, 0],
      armL: [-1.0, 0, -0.3], foreL: [-0.25, 0, 0], staff: [1.3, 0, 0.15], armR: [-1.35, 0, 0.55], foreR: [-0.25, 0, 0],
    };
    timeline(b, t, [
      [0, ST],
      [0.25, { ...ST, body: [0.06, 0, 0, 0, -0.04, 0], chest: [0.3, 0, 0], head: [0.3, 0, 0], jaw: [0.02, 0, 0], armL: [0.1, 0, 0.05], armR: [0.25, 0, -0.1], foreR: [-0.3, 0, 0] }, outQuad],
      [0.5, up, outBack],
      [0.95, { ...up, chest: [-0.28, 0, 0], head: [-0.68, 0.05, 0], jaw: [0.7, 0, 0], armL: [-0.95, 0, -0.28] }],
      [1.3, ST, inOut],
    ], { head: 0.03, jaw: 0.02, foreR: 0.03, staff: 0.04 });
    // The note held: a tremor through the body and the jaw.
    const tr = envelope(t, 0.5, 0.56, 0.9, 1.0) * Math.sin(t * 64);
    b.jaw.rot(0.04 * tr);
    b.armR.rot(0.02 * tr, 0, 0.02 * tr);
    b.staff.rot(0, 0, 0.02 * tr);
    // The light gutters in, flares past full, holds and draws back.
    const k = t < 0.3 ? 1 - 0.35 * span(t, 0, 0.25) : t < 0.55 ? 0.65 + 2.6 * span(t, 0.3, 0.55, outBack) : 3.25 - 2.25 * span(t, 0.9, 1.28, inOut);
    b.core.scale(k * (1 + 0.08 * tr));
    // The bones: drawn up and spun fast (a whole number of turns, so they end where they began), then settle.
    b.orbit.rot(0, TAU * 2 * span(t, 0.25, 1.3, inOut), 0).pos(0, 0.22 * envelope(t, 0.3, 0.6, 0.9, 1.25, outCubic), 0).scale(1 + 0.25 * envelope(t, 0.3, 0.6, 0.9, 1.25));
  }, (t) => {
    const k = envelope(t, 0.35, 0.5, 0.9, 1.2, outCubic);
    return [1 - 0.015 * k, 1 + 0.035 * k, 1 - 0.015 * k];
  }, 'Levantar');

/** Its clips (see models/bonesinger.ts for the bones). */
export const bonesingerClips = (): Clip[] => [idle(), walk(), attack(), hit(), stun(), die(), raise()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = { bonesinger: bonesingerClips };
